import { useEffect, useMemo, useState, useCallback } from "react";
import {
  api,
  InventoryRow,
  InventoryStats,
  Category,
  ItemUpdate,
  ItemDetail,
} from "../api";
import { formatVND, formatNumber, formatDate } from "../utils";
import {
  GlassPanel,
  Badge,
  KpiCard,
  SkeletonTable,
  ErrorState,
  DataTable,
  Modal,
  toast,
  confirm,
} from "../components";
import type { Column } from "../components";
import BarcodeScanner from "../components/BarcodeScanner";
import { useAutoRefresh, useResyncListener } from "../hooks";
import { searchItems } from "../search";

type Mode = "all" | "low" | "out";
type SortMode = "qty_asc" | "qty_desc" | "name_asc" | "name_desc";
type Ownership = "all" | "mine" | "consignment";

type EditField = "sku" | "default_price" | "cost" | "barcode" | "in_stock";

interface VariantForm {
  variant_id?: string;
  variant_name: string;
  sku: string;
  barcode: string;
  default_price: number;
  cost: number;
  initial_stock: number;
  supplier_name: string;
}

interface ProductForm {
  item_name: string;
  description: string;
  category_id: string;
  image_url: string;
  track_stock: boolean;
  color: string;
  is_vat: boolean;
  is_bangke: boolean;
  variants: VariantForm[];
}

const emptyVariant = (): VariantForm => ({
  variant_name: "",
  sku: "",
  barcode: "",
  default_price: 0,
  cost: 0,
  initial_stock: 0,
  supplier_name: "",
});

const emptyForm = (): ProductForm => ({
  item_name: "",
  description: "",
  category_id: "",
  image_url: "",
  track_stock: true,
  color: "",
  is_vat: false,
  is_bangke: false,
  variants: [emptyVariant()],
});

const removeVietnameseAccents = (str: string): string =>
  str.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D");

const slugifySKU = (name: string, variantName?: string): string => {
  const base = removeVietnameseAccents(name).toUpperCase().replace(/[^A-Z0-9]+/g, "").slice(0, 6) || "SKU";
  const vSlug = variantName
    ? "-" + removeVietnameseAccents(variantName).toUpperCase().replace(/[^A-Z0-9]+/g, "").slice(0, 4)
    : "";
  const stamp = Date.now().toString().slice(-5);
  return `${base}${vSlug}-${stamp}`;
};

export default function InventoryPage() {
  const [rows, setRows] = useState<InventoryRow[]>([]);
  const [stats, setStats] = useState<InventoryStats | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("all");
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [category, setCategory] = useState(() => localStorage.getItem("inv_category") || "all");
  const [sortMode, setSortMode] = useState<SortMode>(
    () => (localStorage.getItem("inv_sort_mode") as SortMode) || "qty_asc",
  );
  const [ownership, setOwnership] = useState<Ownership>(
    () => (localStorage.getItem("inv_ownership") as Ownership) || "all",
  );
  const [consignmentNames, setConsignmentNames] = useState<Set<string>>(new Set());
  const [editingCell, setEditingCell] = useState<{
    itemId: string;
    field: EditField;
    value: string;
    variantId: string;
  } | null>(null);

  const [showEdit, setShowEdit] = useState(false);
  const [editDetail, setEditDetail] = useState<ItemDetail | null>(null);
  const [form, setForm] = useState<ProductForm>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<ItemDetail["audit_log"]>([]);

  const advancedFilterActive = ownership !== "all" || sortMode !== "qty_asc";

  const refreshStats = useCallback(() => {
    api.inventoryStats().then(setStats).catch(() => {});
  }, []);

  const fetchParams = useCallback(() => {
    const lowFlag: 0 | 1 = mode === "all" ? 0 : 1;
    const threshold = mode === "out" ? 0 : 5;
    return { low_stock: lowFlag, threshold, limit: 300 };
  }, [mode]);

  const reloadRows = useCallback((silent = false) => {
    const p = fetchParams();
    if (!silent) setLoading(true);
    return Promise.all([api.inventory(p), api.inventoryStats()])
      .then(([inv, s]) => {
        setRows(inv.inventory);
        setStats(s);
      })
      .catch((e) => { if (!silent) setError(e.message); })
      .finally(() => { if (!silent) setLoading(false); });
  }, [fetchParams]);

  useEffect(() => {
    api.consignment
      .list()
      .then((res) => {
        const names = new Set<string>(res.entries.map((e) => e.item_name.toLowerCase()));
        setConsignmentNames(names);
      })
      .catch(() => {});
    api.categories.list().then((r) => setCategories(r.categories)).catch(() => {});
  }, []);

  useEffect(() => {
    reloadRows(false);
  }, [mode, fetchParams, reloadRows]);

  useAutoRefresh(() => {
    reloadRows(true);
  }, 30000);

  useResyncListener(() => {
    reloadRows(true);
  });

  const startEdit = (r: InventoryRow, field: EditField, currentValue: unknown) => {
    if (!r.variant_id) {
      toast.error("Không tìm thấy biến thể, dùng modal Sửa");
      return;
    }
    setEditingCell({
      itemId: r.item_id,
      field,
      value: String(currentValue ?? ""),
      variantId: r.variant_id,
    });
  };

  const cancelEdit = () => setEditingCell(null);

  const saveEdit = async () => {
    if (!editingCell) return;
    const { itemId, field, value, variantId } = editingCell;
    const row = rows.find((x) => x.variant_id === variantId);
    if (!row) {
      setEditingCell(null);
      return;
    }

    if (field === "in_stock") {
      const parsed = Number(value);
      if (Number.isNaN(parsed) || !Number.isInteger(parsed) || parsed < 0) {
        toast.error("Tồn kho phải là số nguyên ≥ 0");
        return;
      }
      const newStock = parsed;
      const oldStock = Number(row.in_stock || 0);
      const delta = newStock - oldStock;
      if (delta === 0) {
        setEditingCell(null);
        return;
      }
      setRows((prev) =>
        prev.map((r) => (r.variant_id === variantId ? { ...r, in_stock: newStock } : r)),
      );
      setEditingCell(null);
      try {
        const res = await api.inventoryAdjust({
          item_id: itemId,
          variant_id: variantId,
          delta,
          reason: "inline edit",
        });
        setRows((prev) =>
          prev.map((r) => (r.variant_id === variantId ? { ...r, in_stock: res.new_stock } : r)),
        );
        toast.success("Đã cập nhật tồn");
        refreshStats();
      } catch (e: any) {
        toast.error(e.message || "Lỗi cập nhật");
        reloadRows(true);
      }
      return;
    }

    if (field === "sku" || field === "barcode") {
      const trimmed = value.trim();
      if (field === "sku" && !trimmed) {
        toast.error("SKU không được để trống");
        return;
      }
      const cur = field === "sku" ? (row.sku || "") : (row.barcode || "");
      if (trimmed === cur) {
        setEditingCell(null);
        return;
      }
      setRows((prev) =>
        prev.map((r) =>
          r.variant_id === variantId ? { ...r, [field]: trimmed || null } : r,
        ),
      );
      setEditingCell(null);
      try {
        await api.items.update(itemId, {
          variants: [{ variant_id: variantId, [field]: trimmed }],
        });
        toast.success("Đã lưu");
      } catch (e: any) {
        toast.error(e.message || "Lỗi cập nhật");
        reloadRows(true);
      }
      return;
    }

    if (field === "default_price" || field === "cost") {
      const num = Number(value);
      if (Number.isNaN(num) || num < 0) {
        toast.error("Giá phải là số ≥ 0");
        return;
      }
      const cur = Number((row as any)[field] ?? 0);
      if (num === cur) {
        setEditingCell(null);
        return;
      }
      setRows((prev) =>
        prev.map((r) => (r.variant_id === variantId ? { ...r, [field]: num } : r)),
      );
      setEditingCell(null);
      try {
        await api.items.update(itemId, {
          variants: [{ variant_id: variantId, [field]: num }],
        });
        toast.success("Đã lưu");
        refreshStats();
      } catch (e: any) {
        toast.error(e.message || "Lỗi cập nhật");
        reloadRows(true);
      }
    }
  };

  const quickAdjust = async (r: InventoryRow, delta: number) => {
    if (!r.variant_id) return;
    const oldStock = Number(r.in_stock || 0);
    const newStock = oldStock + delta;
    if (newStock < 0) return;

    setRows((prev) =>
      prev.map((x) => (x.variant_id === r.variant_id ? { ...x, in_stock: newStock } : x)),
    );
    try {
      const res = await api.inventoryAdjust({
        item_id: r.item_id,
        variant_id: r.variant_id,
        delta,
        reason: "quick adjust",
      });
      setRows((prev) =>
        prev.map((x) => (x.variant_id === r.variant_id ? { ...x, in_stock: res.new_stock } : x)),
      );
      toast.success(delta > 0 ? "+1 tồn" : "−1 tồn");
      refreshStats();
    } catch (e: any) {
      toast.error(e.message || "Lỗi");
      reloadRows(true);
    }
  };

  const openEdit = async (itemId: string) => {
    try {
      const detail = await api.items.detail(itemId);
      setEditDetail(detail);
      setForm({
        item_name: (detail.item as any).item_name || "",
        description: (detail.item as any).description || "",
        category_id: (detail.item as any).category_id || "",
        image_url: (detail.item as any).image_url || "",
        track_stock: !!(detail.item as any).track_stock,
        color: (detail.item as any).color || "",
        is_vat: !!(detail.variants[0]?.is_vat),
        is_bangke: !!(detail.variants[0]?.is_bangke),
        variants: detail.variants.map((v) => ({
          variant_id: v.variant_id,
          variant_name: (v as any).variant_name || "",
          sku: v.sku || "",
          barcode: v.barcode || "",
          default_price: v.default_price || 0,
          cost: v.cost || 0,
          initial_stock: v.in_stock || 0,
          supplier_name: "",
        })),
      });
      setShowEdit(true);
    } catch (e: any) {
      toast.error(e.message || "Lỗi tải chi tiết SP");
    }
  };

  const handleEdit = async () => {
    if (!editDetail) return;
    if (!form.item_name.trim()) {
      toast.error("Nhập tên sản phẩm");
      return;
    }
    setSubmitting(true);
    try {
      const payload: ItemUpdate = {
        item_name: form.item_name,
        description: form.description || undefined,
        category_id: form.category_id || undefined,
        image_url: form.image_url || undefined,
        track_stock: form.track_stock,
        color: form.color || undefined,
        is_vat: form.is_vat ? 1 : 0,
        is_bangke: form.is_bangke ? 1 : 0,
        variants: form.variants.map((v) => ({
          variant_id: v.variant_id,
          variant_name: v.variant_name || undefined,
          sku: v.sku.trim() || slugifySKU(form.item_name, v.variant_name),
          barcode: v.barcode.trim() || undefined,
          default_price: v.default_price,
          cost: v.cost || undefined,
          initial_stock: v.initial_stock,
        })),
      };
      const res = await api.items.update((editDetail.item as any).id, payload);
      const warnings = (res as any).inventory_warnings;
      if (warnings?.length > 0) {
        toast.warning(`Đã lưu local. ${warnings.length} variant chưa sync Loyverse.`);
      } else {
        toast.success("Đã cập nhật sản phẩm");
      }
      setShowEdit(false);
      setEditDetail(null);
      setForm(emptyForm());
      await reloadRows(true);
    } catch (e: any) {
      toast.error(e.message || "Lỗi cập nhật SP");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (itemId: string, name: string) => {
    if (
      !(await confirm({
        title: "Xoá sản phẩm",
        message: `Xoá sản phẩm "${name}"?`,
        danger: true,
        confirmLabel: "Xoá",
      }))
    )
      return;
    try {
      await api.items.delete(itemId);
      toast.success("Đã xoá sản phẩm");
      setRows((prev) => prev.filter((r) => r.item_id !== itemId));
      refreshStats();
    } catch (e: any) {
      toast.error(e.message || "Lỗi xoá");
    }
  };

  const openHistory = async (itemId: string) => {
    try {
      const detail = await api.items.detail(itemId);
      setHistoryData(detail.audit_log);
      setShowHistory(itemId);
    } catch {
      setHistoryData([]);
      setShowHistory(itemId);
    }
  };

  const updateVariant = (idx: number, field: string, value: unknown) => {
    setForm((prev) => {
      const variants = [...prev.variants];
      variants[idx] = { ...variants[idx], [field]: value };
      return { ...prev, variants };
    });
  };

  const removeVariant = (idx: number) => {
    setForm((prev) => ({ ...prev, variants: prev.variants.filter((_, i) => i !== idx) }));
  };

  const categoriesFromRows = useMemo(() => {
    const map = new Map<string, string>();
    rows.forEach((r) => {
      if (r.category_id && r.category_name) map.set(r.category_id, r.category_name);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "vi"));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let result = mode === "out" ? rows.filter((r) => r.in_stock <= 0) : rows;

    if (search.trim()) {
      result = searchItems(result, search, (r) => ({
        id: r.item_id,
        barcode: r.barcode,
        sku: r.sku,
        name: r.item_name,
      })).map(({ item }) => item);
    }

    if (category !== "all") {
      result = result.filter((r) => r.category_id === category);
    }

    if (ownership === "consignment") {
      result = result.filter((r) => consignmentNames.has(r.item_name.toLowerCase()));
    } else if (ownership === "mine") {
      result = result.filter((r) => !consignmentNames.has(r.item_name.toLowerCase()));
    }

    return [...result].sort((a, b) => {
      if (sortMode === "qty_asc") return a.in_stock - b.in_stock;
      if (sortMode === "qty_desc") return b.in_stock - a.in_stock;
      if (sortMode === "name_asc") return a.item_name.localeCompare(b.item_name, "vi");
      if (sortMode === "name_desc") return b.item_name.localeCompare(a.item_name, "vi");
      return 0;
    });
  }, [rows, mode, search, category, ownership, sortMode, consignmentNames]);

  const renderEditableCell = (
    r: InventoryRow,
    field: EditField,
    display: React.ReactNode,
    type: "text" | "number" = "text",
  ) => {
    const isEditing =
      editingCell?.itemId === r.item_id &&
      editingCell?.field === field &&
      editingCell?.variantId === r.variant_id;
    if (isEditing) {
      return (
        <input
          className="form-input"
          autoFocus
          type={type}
          inputMode={type === "number" ? "numeric" : undefined}
          min={type === "number" ? 0 : undefined}
          value={editingCell!.value}
          onChange={(e) => setEditingCell({ ...editingCell!, value: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") saveEdit();
            else if (e.key === "Escape") cancelEdit();
          }}
          onBlur={saveEdit}
          style={{ width: "100%", padding: "4px 8px", fontSize: 13, minHeight: 36, textAlign: type === "number" ? "right" : undefined }}
        />
      );
    }
    const cur = (r as any)[field];
    return (
      <div
        onClick={() => startEdit(r, field, cur)}
        style={{ cursor: "pointer", padding: "8px 4px", minHeight: 36, borderRadius: 4 }}
        title="Click để sửa"
      >
        {display}
      </div>
    );
  };

  const inventoryColumns: Column<InventoryRow>[] = useMemo(
    () => [
      {
        key: "item_name",
        header: "Sản phẩm",
        sortable: true,
        render: (r) => (
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.item_name}</div>
            {r.variant_name && (
              <div className="meta" style={{ marginTop: 2, fontSize: 11 }}>
                {r.variant_name}
              </div>
            )}
          </div>
        ),
      },
      {
        key: "category_name",
        header: "Danh mục",
        render: (r) => (
          <span style={{ fontSize: 13, color: r.category_name ? "var(--text-primary)" : "var(--text-muted)" }}>
            {r.category_name || "—"}
          </span>
        ),
      },
      {
        key: "sku",
        header: "SKU",
        sortable: true,
        render: (r) =>
          renderEditableCell(
            r,
            "sku",
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{r.sku || "—"}</span>,
          ),
      },
      {
        key: "barcode",
        header: "Barcode",
        render: (r) =>
          renderEditableCell(
            r,
            "barcode",
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{r.barcode || "—"}</span>,
          ),
      },
      {
        key: "default_price",
        header: "Giá bán",
        align: "right",
        sortable: true,
        render: (r) =>
          renderEditableCell(
            r,
            "default_price",
            <span style={{ fontWeight: 500 }}>{formatVND(r.default_price)}</span>,
            "number",
          ),
      },
      {
        key: "cost",
        header: "Giá vốn",
        align: "right",
        sortable: true,
        render: (r) =>
          renderEditableCell(
            r,
            "cost",
            <span>{r.cost != null ? formatVND(r.cost) : "—"}</span>,
            "number",
          ),
      },
      {
        key: "margin",
        header: "Margin",
        align: "right",
        render: (r) => {
          const price = Number(r.default_price || 0);
          const cost = Number(r.cost || 0);
          if (!price || !cost) return <span style={{ opacity: 0.4 }}>—</span>;
          const m = ((price - cost) / price) * 100;
          const color =
            m < 10 ? "var(--accent-red)" : m < 30 ? "var(--accent-orange)" : "var(--accent-green)";
          return (
            <span style={{ color, fontWeight: 500, fontSize: 12 }}>{m.toFixed(1)}%</span>
          );
        },
      },
      {
        key: "stock_value",
        header: "Giá trị tồn",
        align: "right",
        render: (r) => {
          const cost = Number(r.cost || 0);
          const val = Number(r.in_stock || 0) * cost;
          return <span style={{ fontSize: 12 }}>{cost ? formatVND(val) : "—"}</span>;
        },
      },
      {
        key: "in_stock",
        header: "Tồn kho",
        sortable: true,
        align: "right",
        render: (r) => {
          const isEditing =
            editingCell?.variantId === r.variant_id && editingCell?.field === "in_stock";
          const low = r.in_stock <= 5;
          const negative = r.in_stock < 0;
          const out = r.in_stock <= 0;
          const btnStyle: React.CSSProperties = {
            width: 28,
            height: 28,
            padding: 0,
            border: "1px solid var(--border)",
            borderRadius: 6,
            background: "var(--bg-tertiary)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            lineHeight: 1,
            flexShrink: 0,
          };
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                style={{ ...btnStyle, opacity: r.in_stock <= 0 ? 0.4 : 1 }}
                disabled={r.in_stock <= 0}
                title="Giảm 1"
                aria-label="Giảm tồn 1"
                onClick={(e) => {
                  e.stopPropagation();
                  quickAdjust(r, -1);
                }}
              >
                −
              </button>
              {isEditing ? (
                <input
                  className="form-input"
                  autoFocus
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={1}
                  value={editingCell!.value}
                  onChange={(e) => setEditingCell({ ...editingCell!, value: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveEdit();
                    else if (e.key === "Escape") cancelEdit();
                  }}
                  onBlur={saveEdit}
                  style={{ width: 56, padding: "4px 6px", fontSize: 13, minHeight: 36, textAlign: "right" }}
                />
              ) : (
                <div
                  onClick={() => startEdit(r, "in_stock", r.in_stock)}
                  style={{
                    cursor: "pointer",
                    padding: "6px 4px",
                    minHeight: 36,
                    borderRadius: 4,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flex: 1,
                    justifyContent: "flex-end",
                  }}
                  title="Click để sửa"
                >
                  <span
                    style={{
                      fontWeight: 700,
                      color: low ? "var(--accent-red)" : "var(--text-primary)",
                    }}
                  >
                    {formatNumber(r.in_stock)}
                  </span>
                  {negative ? (
                    <Badge variant="bad">âm kho</Badge>
                  ) : out ? (
                    <Badge variant="bad">hết</Badge>
                  ) : low ? (
                    <Badge variant="warn">sắp hết</Badge>
                  ) : null}
                </div>
              )}
              <button
                type="button"
                style={btnStyle}
                title="Tăng 1"
                aria-label="Tăng tồn 1"
                onClick={(e) => {
                  e.stopPropagation();
                  quickAdjust(r, 1);
                }}
              >
                +
              </button>
            </div>
          );
        },
      },
      {
        key: "stock_updated_at",
        header: "Cập nhật",
        sortable: true,
        render: (r) => (r.stock_updated_at ? formatDate(r.stock_updated_at) : "—"),
      },
      {
        key: "actions",
        header: "",
        align: "right",
        render: (r) => (
          <div style={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <button
              className="btn btn-ghost"
              style={{ padding: "4px 6px" }}
              onClick={() => openEdit(r.item_id)}
              title="Sửa"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                edit
              </span>
            </button>
            <button
              className="btn btn-ghost"
              style={{ padding: "4px 6px" }}
              onClick={() => openHistory(r.item_id)}
              title="Lịch sử"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                history
              </span>
            </button>
            <button
              className="btn btn-ghost"
              style={{ padding: "4px 6px", color: "var(--accent-red)" }}
              onClick={() => handleDelete(r.item_id, r.item_name)}
              title="Xoá"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                delete
              </span>
            </button>
          </div>
        ),
      },
    ],
    [editingCell, rows],
  );

  const renderFormBody = () => (
    <>
      <div className="form-section">
        <div className="form-section-title">
          <span className="material-symbols-outlined">inventory_2</span>
          Thông tin cơ bản
        </div>
        <div className="form-group">
          <label>
            Tên sản phẩm <span className="required">*</span>
          </label>
          <input
            placeholder="Ví dụ: Áo thun cotton"
            value={form.item_name}
            onChange={(e) => setForm({ ...form, item_name: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Mô tả</label>
          <textarea
            placeholder="Ghi chú nội bộ..."
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
          />
        </div>
        <div className="form-group">
          <label>Danh mục</label>
          <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
            <option value="">— Không nhóm —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-title">
          <span className="material-symbols-outlined">receipt_long</span>
          Thuế & quản lý kho
        </div>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={form.track_stock}
              onChange={(e) => setForm({ ...form, track_stock: e.target.checked })}
            />
            Theo dõi tồn kho
          </label>
          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={form.is_vat}
              onChange={(e) => setForm({ ...form, is_vat: e.target.checked })}
            />
            Có VAT
          </label>
          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={form.is_bangke}
              onChange={(e) => setForm({ ...form, is_bangke: e.target.checked })}
            />
            Bảng kê 01/TNDN
          </label>
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-title" style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="material-symbols-outlined">tune</span>
            Biến thể & giá ({form.variants.length})
          </span>
          <button
            className="btn btn-ghost"
            style={{ fontSize: 12, padding: "4px 10px" }}
            onClick={() => setForm({ ...form, variants: [...form.variants, emptyVariant()] })}
            type="button"
          >
            + Thêm biến thể
          </button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="variant-table">
            <thead>
              <tr>
                <th>Tên BT</th>
                <th>SKU</th>
                <th>Barcode</th>
                <th className="num">Giá vốn</th>
                <th className="num">Giá bán *</th>
                <th className="num">Tồn kho</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {form.variants.map((v, idx) => (
                <tr key={idx}>
                  <td data-label="Tên BT">
                    <input
                      placeholder="Đỏ / Size M"
                      value={v.variant_name}
                      onChange={(e) => updateVariant(idx, "variant_name", e.target.value)}
                    />
                  </td>
                  <td data-label="SKU">
                    <input value={v.sku} onChange={(e) => updateVariant(idx, "sku", e.target.value)} />
                  </td>
                  <td data-label="Barcode">
                    <input value={v.barcode} onChange={(e) => updateVariant(idx, "barcode", e.target.value)} />
                  </td>
                  <td data-label="Giá vốn">
                    <input
                      type="number"
                      inputMode="numeric"
                      className="num"
                      value={v.cost || ""}
                      onChange={(e) => updateVariant(idx, "cost", Number(e.target.value))}
                    />
                  </td>
                  <td data-label="Giá bán *">
                    <input
                      type="number"
                      inputMode="numeric"
                      className="num"
                      value={v.default_price || ""}
                      onChange={(e) => updateVariant(idx, "default_price", Number(e.target.value))}
                    />
                  </td>
                  <td data-label="Tồn kho">
                    <input
                      type="number"
                      inputMode="numeric"
                      className="num"
                      value={v.initial_stock || ""}
                      onChange={(e) => updateVariant(idx, "initial_stock", Number(e.target.value))}
                    />
                  </td>
                  <td data-label="">
                    <button
                      className="btn-remove"
                      onClick={() => removeVariant(idx)}
                      type="button"
                      title="Xóa biến thể này"
                      disabled={form.variants.length <= 1}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                        delete
                      </span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  return (
    <>
      {stats && (
        <div className="kpi-grid">
          <KpiCard
            label="Tổng biến thể"
            value={formatNumber(stats.variants)}
            note="đang theo dõi"
            dotColor="blue"
          />
          <KpiCard
            label="Sắp hết (≤ 5)"
            value={formatNumber(stats.low_stock)}
            note="cần nhập thêm"
            dotColor={stats.low_stock > 0 ? "warn" : ""}
          />
          <KpiCard
            label="Hết hàng"
            value={formatNumber(stats.out_of_stock)}
            note="≤ 0 tồn"
            dotColor={stats.out_of_stock > 0 ? "bad" : ""}
          />
          <KpiCard
            label="Giá trị tồn theo vốn"
            value={formatVND(stats.stock_cost_value)}
            note={`bán lẻ ${formatVND(stats.stock_retail_value)}`}
            dotColor="ok"
          />
        </div>
      )}

      <GlassPanel
        title="Tồn kho real-time"
        badge={`${filteredRows.length} biến thể`}
        badgeVariant={mode === "all" ? "info" : mode === "low" ? "warn" : "bad"}
      >
        <div className="tabs" style={{ marginBottom: 16 }}>
          <div className={`tab-item ${mode === "all" ? "active" : ""}`} onClick={() => setMode("all")}>
            Tất cả
          </div>
          <div className={`tab-item ${mode === "low" ? "active" : ""}`} onClick={() => setMode("low")}>
            ⚠ Sắp hết (≤ 5)
          </div>
          <div className={`tab-item ${mode === "out" ? "active" : ""}`} onClick={() => setMode("out")}>
            ⛔ Hết hàng
          </div>
        </div>

        <div className="filter-bar" style={{ flexWrap: "wrap" }}>
          <div className="search" style={{ flex: 1, minWidth: 160 }}>
            <span className="material-symbols-outlined">search</span>
            <input
              placeholder="Tìm sản phẩm, SKU, barcode..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              localStorage.setItem("inv_category", e.target.value);
            }}
            style={{
              minWidth: 120,
              maxWidth: 180,
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid var(--border)",
              background: "var(--glass-bg)",
              color: "var(--text-primary)",
              fontSize: 13,
            }}
            aria-label="Lọc danh mục"
          >
            <option value="all">Danh mục ({categoriesFromRows.length})</option>
            {categoriesFromRows.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <button className="btn-icon" onClick={() => setScannerOpen(true)} title="Quét barcode" aria-label="Quét barcode">
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
              qr_code_scanner
            </span>
          </button>
          <button
            className="btn-icon"
            onClick={async () => {
              setRefreshing(true);
              try {
                await reloadRows(true);
                toast.success("Đã cập nhật");
              } catch {
                /* ignore */
              }
              setRefreshing(false);
            }}
            title="Làm mới"
            aria-label="Làm mới"
            disabled={refreshing}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 18, animation: refreshing ? "spin 1s linear infinite" : undefined }}
            >
              refresh
            </span>
          </button>
          <button
            className={`btn-icon ${advancedFilterActive ? "active" : ""}`}
            onClick={() => setShowAdvancedFilter((v) => !v)}
            title="Bộ lọc nâng cao"
            aria-label="Bộ lọc nâng cao"
            style={advancedFilterActive ? { background: "var(--accent-blue)", color: "#fff" } : undefined}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
              tune
            </span>
          </button>
        </div>

        {showAdvancedFilter && (
          <div className="filter-advanced">
            <div className="filter-field">
              <label>Sắp xếp</label>
              <select
                value={sortMode}
                onChange={(e) => {
                  setSortMode(e.target.value as SortMode);
                  localStorage.setItem("inv_sort_mode", e.target.value);
                }}
              >
                <option value="qty_asc">Số lượng: Ít → Nhiều</option>
                <option value="qty_desc">Số lượng: Nhiều → Ít</option>
                <option value="name_asc">Tên: A → Z</option>
                <option value="name_desc">Tên: Z → A</option>
              </select>
            </div>
            <div className="filter-field">
              <label>Nguồn hàng</label>
              <select
                value={ownership}
                onChange={(e) => {
                  setOwnership(e.target.value as Ownership);
                  localStorage.setItem("inv_ownership", e.target.value);
                }}
              >
                <option value="all">Tất cả</option>
                <option value="mine">Hàng mình</option>
                <option value="consignment">Hàng ký gửi</option>
              </select>
            </div>
            {advancedFilterActive && (
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setSortMode("qty_asc");
                  localStorage.setItem("inv_sort_mode", "qty_asc");
                  setOwnership("all");
                  localStorage.setItem("inv_ownership", "all");
                }}
              >
                Xoá lọc
              </button>
            )}
          </div>
        )}

        {loading ? (
          <SkeletonTable rows={8} cols={8} />
        ) : error ? (
          <ErrorState
            error={error}
            onRetry={() => {
              setError(null);
              setLoading(true);
              reloadRows(true)
                .catch((e) => setError(e.message))
                .finally(() => setLoading(false));
            }}
          />
        ) : filteredRows.length === 0 ? (
          <div className="empty-state">
            <span className="material-symbols-outlined" style={{ fontSize: 48, opacity: 0.4 }}>
              {mode === "out" ? "check_circle" : "inventory_2"}
            </span>
            <p style={{ margin: 0, fontSize: 14 }}>
              {mode === "out"
                ? "Không còn sản phẩm nào hết hàng — tuyệt vời!"
                : "Không có sản phẩm phù hợp bộ lọc"}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <DataTable columns={inventoryColumns} rows={filteredRows} rowKey={(r) => r.variant_id} pageSize={50} />
          </div>
        )}
      </GlassPanel>

      <Modal
        open={showEdit && !!editDetail}
        onClose={() => {
          setShowEdit(false);
          setEditDetail(null);
        }}
        title="Sửa sản phẩm"
        width={760}
        footer={
          <>
            <button
              className="btn btn-ghost"
              onClick={() => {
                setShowEdit(false);
                setEditDetail(null);
              }}
            >
              Huỷ
            </button>
            <button className="btn" onClick={handleEdit} disabled={submitting}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                save
              </span>
              {submitting ? "Đang lưu..." : "Cập nhật"}
            </button>
          </>
        }
      >
        {renderFormBody()}
      </Modal>

      <Modal open={!!showHistory} onClose={() => setShowHistory(null)} title="Lịch sử thay đổi" width={520}>
        <div style={{ maxHeight: "60vh", overflowY: "auto", fontSize: 12 }}>
          {historyData.length === 0 ? (
            <div style={{ padding: 16, textAlign: "center", opacity: 0.6 }}>Chưa có lịch sử</div>
          ) : (
            historyData.map((log) => (
              <div key={log.id} style={{ padding: "6px 0", borderBottom: "1px solid var(--border-subtle)" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontWeight: 500 }}>{log.action}</span>
                  <span style={{ opacity: 0.6 }}>{formatDate(log.changed_at)}</span>
                </div>
                {log.field_changed && (
                  <div style={{ color: "var(--text-secondary)" }}>
                    {log.field_changed}: {log.old_value} → {log.new_value}
                  </div>
                )}
                <div style={{ opacity: 0.5 }}>
                  {log.changed_by} ({log.source})
                </div>
              </div>
            ))
          )}
        </div>
      </Modal>

      {scannerOpen && (
        <BarcodeScanner
          onScan={(code) => {
            setSearch(code);
            setScannerOpen(false);
          }}
          onClose={() => setScannerOpen(false)}
        />
      )}
    </>
  );
}
